// covered-by: packages/schemas/src/__tests__/knowledge-rule-schema.spec.ts
import { z } from "zod";

import {
  KnowledgeScopeSource,
  knowledgeRuleCitationSchema,
  knowledgeScopeSchema,
} from "./knowledge";

const timeStamps = z.object({
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * A knowledge rule. Rules are created ONLY by the rembrandt extraction /
 * synthesis workflow — there is no manual create/edit path. Admin mutations
 * are limited to status transitions (promote/reject/archive) and scope edits.
 *
 * Suggestions are rules with `status=suggestion` (low-trust origin with
 * enough confidence); promotion is a status transition so the _id, citations,
 * and audit chain survive.
 *
 * Raw extractions live in the rembrandt-owned, snake_case, append-only
 * `knowledge_extractions` collection (no mongoose model — christo never
 * reads it this phase); `citations[].extractionId` back-references it.
 */
export enum KnowledgeRuleStatus {
  SUGGESTION = "suggestion",
  ACTIVE = "active",
  ARCHIVED = "archived",
  REJECTED = "rejected",
}

export enum KnowledgeRuleOrigin {
  HIGH_TRUST = "high_trust",
  LOW_TRUST = "low_trust",
}

export enum KnowledgeRuleCategory {
  CLAIM = "claim",
  BRAND = "brand",
  CHECKLIST = "checklist",
  POLICY = "policy",
  OTHER = "other",
}

/**
 * Regulatory risk of using a claim as-worded — a feature-engineered tag
 * dimension retrieval can filter on (e.g. surface only low-risk approved
 * wording, or flag a query that matches a high-risk rule).
 */
export enum KnowledgeRuleRiskLevel {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

/**
 * Feature-engineered tags for a knowledge rule — structured properties BEYOND
 * `scope.brand` that retrieval can filter or boost on (nutrient, claim type,
 * market, regulation, product form, risk). Each array holds normalized tokens
 * (lowercase snake_case, e.g. "vitamin_b6", "sustainability", "eu"). Dimensions
 * are RECOMMENDED, not exhaustive: retrieval uses whichever are present and new
 * dimensions stay additive. The rule's free-text `keywords` complements these
 * for keyword / hybrid matching.
 */
export const knowledgeRuleTagsSchema = z.object({
  nutrients: z.array(z.string()).default([]),
  claimTypes: z.array(z.string()).default([]),
  markets: z.array(z.string()).default([]),
  regulations: z.array(z.string()).default([]),
  productForms: z.array(z.string()).default([]),
  riskLevel: z.nativeEnum(KnowledgeRuleRiskLevel).nullable().optional(),
});

/**
 * Whether the guideline permits the claim it describes: approved as-worded,
 * conditional (permitted only if the qualifiers are met), or prohibited. This
 * is the field that answers "can I claim X?" — a candidate claim matches a
 * rule on subject + attribute, and the polarity decides yes / yes-if / no.
 */
export enum KnowledgeClaimPolarity {
  APPROVED = "approved",
  CONDITIONAL = "conditional",
  PROHIBITED = "prohibited",
}

/**
 * A plain-English decomposition of a claim rule, engineered so a candidate
 * claim can be COMPARED against the guideline instead of relying on embedding
 * similarity alone. The extractor rewrites tabular source (e.g. a Claims
 * Powerhouse row) into `statement` (natural language, not cells), then splits
 * it into what the claim is about (`subjects`), the property it asserts
 * (`attribute`), the conditions attached (`qualifiers`), and whether the
 * guideline permits it (`polarity`). All optional + additive — rules created
 * before this carry none, and comparison falls back to embedding matching.
 */
export const knowledgeRuleClaimSchema = z.object({
  statement: z.string().nullable().optional(),
  subjects: z.array(z.string()).default([]),
  attribute: z.string().nullable().optional(),
  qualifiers: z.array(z.string()).default([]),
  polarity: z.nativeEnum(KnowledgeClaimPolarity).nullable().optional(),
});

/** Declared now; incremented by the later review-integration phase. */
export const knowledgeRuleUsageSchema = z.object({
  reviewCount: z.coerce.number().int().min(0).default(0),
  lastUsedInReviewAt: z.coerce.date().nullable().optional(),
});

export const knowledgeRuleSchema = z
  .object({
    _id: z.string(),
    enterpriseId: z.string(),
    title: z.string().optional(),
    content: z.string().min(1),
    category: z.nativeEnum(KnowledgeRuleCategory),
    status: z.nativeEnum(KnowledgeRuleStatus),
    origin: z.nativeEnum(KnowledgeRuleOrigin),
    confidence: z.coerce.number().min(0).max(1),
    scope: knowledgeScopeSchema.default({}),
    scopeSource: z
      .nativeEnum(KnowledgeScopeSource)
      .default(KnowledgeScopeSource.NONE),
    // Citations are MANDATORY: every rule links to its exact source
    // location(s). Ordered — citations[0] is primary (most trustworthy).
    citations: z.array(knowledgeRuleCitationSchema).min(1),
    usage: knowledgeRuleUsageSchema.default({ reviewCount: 0 }),
    extraction: z
      .object({
        workflowId: z.string().optional(),
        runId: z.string().optional(),
        extractedAt: z.coerce.date().optional(),
        model: z.string().optional(),
      })
      .optional(),
    statusChangedBy: z.string().nullable().optional(),
    statusChangedAt: z.coerce.date().nullable().optional(),
    confirmationCount: z.coerce.number().int().min(0).default(0),
    contradictionCount: z.coerce.number().int().min(0).default(0),
    supersededBy: z.string().nullable().optional(),
    // Feature-engineered tags + keywords for tag-filtered / hybrid retrieval
    // beyond scope.brand. Additive + optional: rules created before tagging
    // carry none, and retrieval treats an absent dimension as "no filter".
    // Written by the rembrandt extraction / tagging pass, never by christo.
    tags: knowledgeRuleTagsSchema.optional(),
    keywords: z.array(z.string()).default([]),
    // Plain-English claim decomposition for guideline comparison (see schema).
    claim: knowledgeRuleClaimSchema.optional(),
    // Lossless per-column provenance from a structured source row (spreadsheet /
    // CSV): the full { columnHeader -> cellValue } map the deterministic
    // extractor read, preserved so the UI can show every field and nothing from
    // the sheet is dropped when `content` is condensed to a concise rule. Free
    // shape (columns vary per customer sheet); the fixed, filterable features
    // stay in `tags` + `scope`. Absent for LLM-extracted / non-tabular rules.
    sourceFields: z.record(z.string(), z.string()).optional(),
  })
  .merge(timeStamps);

export type KnowledgeRule = z.infer<typeof knowledgeRuleSchema>;
export type KnowledgeRuleUsage = z.infer<typeof knowledgeRuleUsageSchema>;
export type KnowledgeRuleTags = z.infer<typeof knowledgeRuleTagsSchema>;
export type KnowledgeRuleClaim = z.infer<typeof knowledgeRuleClaimSchema>;
